/**
 * useKeyboardShortcuts Hook
 *
 * Custom hook for managing keyboard shortcuts in the application.
 * Supports global shortcuts and component-specific shortcuts.
 *
 * Phase 4: Keyboard shortcuts
 */

import { useCallback, useEffect, useMemo, useRef } from "react";

// ============================================================================
// Types
// ============================================================================

export interface ShortcutConfig {
  /** Key combination (e.g., "ctrl+s", "escape", "ctrl+shift+z") */
  key: string;
  /** Handler function */
  handler: (event: KeyboardEvent) => void;
  /** Description for help/documentation */
  description?: string;
  /** Whether to prevent default browser behavior */
  preventDefault?: boolean;
  /** Whether to stop event propagation */
  stopPropagation?: boolean;
  /** Only trigger when an input/textarea is NOT focused */
  ignoreInputs?: boolean;
  /** Only trigger when this element or its children are focused */
  scope?: "global" | "local";
}

export interface KeyboardShortcutsOptions {
  /** Enable/disable all shortcuts */
  enabled?: boolean;
  /** Element to attach listeners to (default: document) */
  target?: HTMLElement | Document | null;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Parse a key string into its components
 * e.g., "ctrl+shift+s" -> { ctrl: true, shift: true, alt: false, meta: false, key: "s" }
 */
function parseKeyCombo(keyCombo: string): {
  ctrl: boolean;
  shift: boolean;
  alt: boolean;
  meta: boolean;
  key: string;
} {
  const parts = keyCombo.toLowerCase().split("+");
  const key = parts.pop() || "";

  return {
    ctrl: parts.includes("ctrl") || parts.includes("control"),
    shift: parts.includes("shift"),
    alt: parts.includes("alt"),
    meta: parts.includes("meta") || parts.includes("cmd") || parts.includes("command"),
    key,
  };
}

/**
 * Check if an event matches a key combination
 */
function matchesKeyCombo(event: KeyboardEvent, keyCombo: string): boolean {
  const { ctrl, shift, alt, meta, key } = parseKeyCombo(keyCombo);

  // Handle special keys
  const eventKey = event.key.toLowerCase();
  const specialKeyMap: Record<string, string> = {
    escape: "escape",
    esc: "escape",
    enter: "enter",
    return: "enter",
    tab: "tab",
    space: " ",
    backspace: "backspace",
    delete: "delete",
    arrowup: "arrowup",
    arrowdown: "arrowdown",
    arrowleft: "arrowleft",
    arrowright: "arrowright",
  };

  const normalizedEventKey = specialKeyMap[eventKey] || eventKey;
  const normalizedKey = specialKeyMap[key] || key;

  return (
    event.ctrlKey === ctrl &&
    event.shiftKey === shift &&
    event.altKey === alt &&
    event.metaKey === meta &&
    normalizedEventKey === normalizedKey
  );
}

/**
 * Check if the current focus is on an input element
 */
function isInputFocused(): boolean {
  const activeElement = document.activeElement;
  if (!activeElement) return false;

  const tagName = activeElement.tagName.toLowerCase();
  const isContentEditable = activeElement.getAttribute("contenteditable") === "true";

  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    isContentEditable
  );
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for managing keyboard shortcuts
 *
 * @example
 * ```tsx
 * useKeyboardShortcuts([
 *   { key: "ctrl+s", handler: handleSave, description: "Save changes" },
 *   { key: "escape", handler: handleCancel, description: "Cancel" },
 * ]);
 * ```
 */
export function useKeyboardShortcuts(
  shortcuts: ShortcutConfig[],
  options: KeyboardShortcutsOptions = {}
): void {
  const { enabled = true, target } = options;
  const shortcutsRef = useRef(shortcuts);

  // Update ref when shortcuts change
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      for (const shortcut of shortcutsRef.current) {
        // Check if key matches
        if (!matchesKeyCombo(event, shortcut.key)) continue;

        // Check if we should ignore inputs
        if (shortcut.ignoreInputs !== false && isInputFocused()) {
          // Allow escape to work even in inputs
          if (shortcut.key.toLowerCase() !== "escape") {
            continue;
          }
        }

        // Execute handler
        if (shortcut.preventDefault !== false) {
          event.preventDefault();
        }
        if (shortcut.stopPropagation) {
          event.stopPropagation();
        }

        shortcut.handler(event);
        break; // Only trigger the first matching shortcut
      }
    },
    [enabled]
  );

  useEffect(() => {
    const targetElement = target ?? document;

    targetElement.addEventListener("keydown", handleKeyDown as EventListener);

    return () => {
      targetElement.removeEventListener("keydown", handleKeyDown as EventListener);
    };
  }, [target, handleKeyDown]);
}

// ============================================================================
// Predefined Shortcut Sets
// ============================================================================

/**
 * Common shortcuts for table/list interactions
 */
export function useTableShortcuts(handlers: {
  onEscape?: () => void;
  onEnter?: () => void;
  onSave?: () => void;
  onDelete?: () => void;
  onSelectAll?: () => void;
  onUp?: () => void;
  onDown?: () => void;
}): void {
  const shortcuts = useMemo<ShortcutConfig[]>(() => {
    const result: ShortcutConfig[] = [];

    if (handlers.onEscape) {
      result.push({
        key: "escape",
        handler: handlers.onEscape,
        description: "Cancel current action",
        ignoreInputs: false, // Allow escape even in inputs
      });
    }

    if (handlers.onEnter) {
      result.push({
        key: "enter",
        handler: handlers.onEnter,
        description: "Confirm/Submit",
      });
    }

    if (handlers.onSave) {
      result.push({
        key: "ctrl+s",
        handler: handlers.onSave,
        description: "Save changes",
      });
    }

    if (handlers.onDelete) {
      result.push({
        key: "delete",
        handler: handlers.onDelete,
        description: "Delete selected",
      });
    }

    if (handlers.onSelectAll) {
      result.push({
        key: "ctrl+a",
        handler: handlers.onSelectAll,
        description: "Select all",
      });
    }

    if (handlers.onUp) {
      result.push({
        key: "arrowup",
        handler: handlers.onUp,
        description: "Move up",
      });
    }

    if (handlers.onDown) {
      result.push({
        key: "arrowdown",
        handler: handlers.onDown,
        description: "Move down",
      });
    }

    return result;
  }, [handlers]);

  useKeyboardShortcuts(shortcuts);
}

/**
 * Shortcuts for modal dialogs
 */
export function useModalShortcuts(handlers: {
  onClose: () => void;
  onConfirm?: () => void;
}): void {
  const shortcuts = useMemo<ShortcutConfig[]>(() => {
    const result: ShortcutConfig[] = [
      {
        key: "escape",
        handler: handlers.onClose,
        description: "Close modal",
        ignoreInputs: false,
      },
    ];

    if (handlers.onConfirm) {
      result.push({
        key: "ctrl+enter",
        handler: handlers.onConfirm,
        description: "Confirm action",
      });
    }

    return result;
  }, [handlers]);

  useKeyboardShortcuts(shortcuts);
}

/**
 * Shortcuts for form interactions
 */
export function useFormShortcuts(handlers: {
  onSubmit?: () => void;
  onCancel?: () => void;
  onReset?: () => void;
}): void {
  const shortcuts = useMemo<ShortcutConfig[]>(() => {
    const result: ShortcutConfig[] = [];

    if (handlers.onSubmit) {
      result.push({
        key: "ctrl+enter",
        handler: handlers.onSubmit,
        description: "Submit form",
      });
    }

    if (handlers.onCancel) {
      result.push({
        key: "escape",
        handler: handlers.onCancel,
        description: "Cancel",
        ignoreInputs: false,
      });
    }

    if (handlers.onReset) {
      result.push({
        key: "ctrl+shift+r",
        handler: handlers.onReset,
        description: "Reset form",
      });
    }

    return result;
  }, [handlers]);

  useKeyboardShortcuts(shortcuts);
}

// ============================================================================
// Keyboard Shortcuts Help Component
// ============================================================================

export interface ShortcutHelpItem {
  key: string;
  description: string;
  category?: string;
}

/**
 * Format a key combination for display
 * e.g., "ctrl+s" -> "Ctrl + S" or "Cmd + S" on Mac
 */
export function formatKeyCombo(keyCombo: string, useMacSymbols = false): string {
  const parts = keyCombo.split("+");

  const formatPart = (part: string): string => {
    const lower = part.toLowerCase();

    if (useMacSymbols) {
      switch (lower) {
        case "ctrl":
        case "control":
          return "\u2303"; // Control symbol
        case "shift":
          return "\u21E7"; // Shift symbol
        case "alt":
          return "\u2325"; // Option symbol
        case "meta":
        case "cmd":
        case "command":
          return "\u2318"; // Command symbol
        case "enter":
        case "return":
          return "\u23CE"; // Enter symbol
        case "escape":
        case "esc":
          return "Esc";
        case "backspace":
          return "\u232B"; // Delete symbol
        case "delete":
          return "\u2326"; // Forward delete
        case "tab":
          return "\u21E5"; // Tab symbol
        default:
          return part.charAt(0).toUpperCase() + part.slice(1);
      }
    }

    switch (lower) {
      case "ctrl":
      case "control":
        return "Ctrl";
      case "shift":
        return "Shift";
      case "alt":
        return "Alt";
      case "meta":
      case "cmd":
      case "command":
        return "Cmd";
      case "escape":
      case "esc":
        return "Esc";
      case "enter":
      case "return":
        return "Enter";
      case "arrowup":
        return "\u2191";
      case "arrowdown":
        return "\u2193";
      case "arrowleft":
        return "\u2190";
      case "arrowright":
        return "\u2192";
      default:
        return part.charAt(0).toUpperCase() + part.slice(1);
    }
  };

  if (useMacSymbols) {
    return parts.map(formatPart).join("");
  }

  return parts.map(formatPart).join(" + ");
}

export default useKeyboardShortcuts;

